import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { FsnPopoverHeaderAction } from '@fusion/components/PopoverHeaderAction';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import Box from '@mui/material/Box';
import { styled, alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material//Grid';
import Avatar from '@mui/material/Avatar';
import { rgba } from 'emotion-rgba';

const classPrefix$2 = 'fsn-user-info-card';
const classes$2 = {
    card: `${classPrefix$2}`,
    cardTitle: `${classPrefix$2}-title`,
    cardContent: `${classPrefix$2}-content`,
    cardItem: `${classPrefix$2}-item`,
    cardContentScroll: `${classPrefix$2}-content-scroll`,
};
const ids$2 = classes$2;
const UserProfileCard = styled(Box)(({ theme }) => ({
    [`&.${classes$2.card}`]: {
        border: `1px solid ${alpha(theme.palette.common.black, 0.2)}`,
        borderTop: `5px solid ${theme.palette.primary.main}`,
        borderRadius: `5px`,
        fontFamily: theme.typography.fontFamily,
    },
    [`& .${classes$2.cardTitle}`]: {
        fontWeight: theme.palette.mode === 'light' ? 'bold' : 'normal',
        fontSize: 20,
    },
    [`& .${classes$2.cardItem}`]: {
        fontSize: theme.typography.subtitle1.fontSize,
    },
    [`& .${classes$2.cardContent}`]: {
        padding: theme.spacing(2, 0, 2, 2),
    },
    [`& .${classes$2.cardContentScroll}`]: {
        maxHeight: 100,
        overflow: 'auto',
    },
}));
/**
 * The User Info Card component provides a card that contains two sections a card title and card content when a user either has site codes or more than one role:
 * - cardTitle a string that will serve as the card's title
 * - cardContent an array of strings that will serve as the card's content
 */
const FsnUserInfoCard = (props) => {
    const { cardTitle, cardContent } = props;
    return (jsx(UserProfileCard, { id: `${ids$2.card}-${cardTitle}`, "data-testid": `${ids$2.card}-${cardTitle}`, className: classes$2.card, children: jsxs(Box, { className: classes$2.cardContent, children: [jsx(Typography, { className: classes$2.cardTitle, variant: "h4", gutterBottom: true, component: "div", children: cardTitle }), jsx(Box, { className: classes$2.cardContentScroll, children: cardContent &&
                        cardContent.map((content, index) => (jsx(Typography, { className: classes$2.cardItem, children: content }, index))) })] }) }));
};

const classPrefix$1 = 'fsn-user-info-profile';
const classes$1 = {
    profile: `${classPrefix$1}`,
    profileImage: `${classPrefix$1}-image`,
    profileLetterBox: `${classPrefix$1}-letter-box`,
    profileUserInfo: `${classPrefix$1}-user-info`,
};
const ids$1 = classes$1;
const GRID_SIZE = 'auto';
const UserProfileContent = styled(Grid)(({ theme }) => ({
    [`&.${classes$1.profile}`]: {
        display: 'flex',
        alignItems: 'center',
        overflow: 'auto',
        backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : `${rgba(theme.palette.common.white, 0.05)}`,
        padding: theme.spacing(2),
    },
    [`& .${classes$1.profileImage}`]: {
        height: 70,
        width: 70,
        borderRadius: '50%',
    },
    [`& .${classes$1.profileLetterBox}`]: {
        height: 80,
        width: 80,
        color: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black,
        backgroundColor: theme.palette.primary.main,
        fontSize: 54,
    },
    [`& .${classes$1.profileUserInfo}`]: {
        fontSize: theme.typography.subtitle1.fontSize,
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.mode === 'dark' ? theme.palette.background : null,
        padding: theme.spacing(0, 0, 0, 2),
        '& span': {
            fontWeight: theme.palette.mode === 'light' ? 'bold' : 'normal',
            ...(theme.palette.mode === 'dark' && {
                color: theme.palette.grey[300],
            }),
        },
    },
}));
/**
 * The User Info Profile Section component provides a table that displays the relevant user information
 * provided in the {@link FsnUser} model and {@link FsnUserInfoLabels}
 *
 * The component provides the following information:
 * - name: User's full display name
 * - email: User's e-mail
 * - gmin: User's GMIN
 * - authorities: list of authorities the user has for the application
 * - ultimateDuns: The Ultimate DUNS this user is associated with
 */
const FsnUserInfoProfileSection = (props) => {
    const { user, labels: { duns = 'Ultimate Duns', email = 'E-mail', gmin = 'GMIN', name = 'Name', role = 'Role' }, } = props;
    return (jsxs(UserProfileContent, { "data-testid": ids$1.profile, className: classes$1.profile, children: [user.profileImageUrl && (jsx(Grid, { "data-testid": classes$1.profileImage, item: true, xs: GRID_SIZE, children: jsx("img", { alt: "user-profile", className: classes$1.profileImage, src: user.profileImageUrl }) })), !user.profileImageUrl && user.displayName && user.displayName.length && (jsx(Avatar, { "data-testid": classes$1.profileLetterBox, className: classes$1.profileLetterBox, variant: "square", children: user.displayName[0].toUpperCase() })), jsxs(Grid, { className: classes$1.profileUserInfo, item: true, xs: GRID_SIZE, children: [jsxs(Grid, { item: true, xs: GRID_SIZE, children: [jsxs("span", { children: [gmin, ":"] }), " ", user.gmin] }), jsxs(Grid, { item: true, xs: GRID_SIZE, children: [jsxs("span", { children: [name, ":"] }), " ", user.displayName] }), jsxs(Grid, { item: true, xs: GRID_SIZE, children: [jsxs("span", { children: [email, ":"] }), " ", user.email] }), user.roles && user.roles.length === 1 && (jsxs(Grid, { item: true, xs: GRID_SIZE, children: [jsxs("span", { children: [role, ":"] }), " ", user.roles[0]] })), user.ultimateDuns && (jsxs(Grid, { item: true, xs: GRID_SIZE, children: [jsxs("span", { children: [duns, ":"] }), " ", user.ultimateDuns] }))] })] }));
};

const classPrefix = 'fsn-user-info';
const classes = {
    root: `${classPrefix}`,
    icon: `${classPrefix}-icon`,
};
const ids = classes;
const headerTitles = {
    siteCodes: 'Site Codes (Duns)',
    roles: 'Roles',
};
const UserInfoContent = styled(Box)(({ theme }) => ({
    [`&.${classes.root}`]: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
}));
/**
 * The User Info component displays the user's profile information.
 * The component provides a popover on the header, with information about the logged-in user.
 * - roles: list of roles the user has for the application
 * - siteCodes: The Site Codes this user is associated with
 */
const FsnUserInfo = (props) => {
    const { user, labels: { aria = 'User Info Header Action', ...profileLabels } = {} } = props;
    return (jsx(FsnPopoverHeaderAction, { ariaLabel: aria, icon: jsx(AccountCircleOutlinedIcon, {}), children: jsxs(UserInfoContent, { "data-testid": classes.root, className: classes.root, children: [user && jsx(FsnUserInfoProfileSection, { labels: profileLabels, user: user }), user && user.siteCodes && user.siteCodes.length > 0 && (jsx(FsnUserInfoCard, { cardTitle: headerTitles.siteCodes, cardContent: user.siteCodes })), user && user.roles && user.roles.length > 1 && (jsx(FsnUserInfoCard, { cardTitle: headerTitles.roles, cardContent: user.roles }))] }) }));
};

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
const testIds = { ...ids, ...ids$2, ...ids$1 };

export { FsnUserInfo, testIds };
